import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Field, Input, TagPicker, Textarea } from "@hulahoop/ui";
import { DecimalUnitInput } from "@/components/shared/forms/DecimalUnitInput";
import { OptionSelect } from "@/components/shared/forms/OptionSelect";
import { RepresentativeFields } from "@/components/shared/forms/RepresentativeFields";
import type { BeneficiaryWizardFormData } from "@/domain/validation/beneficiarySchemas";
import { useRepresentativeLabels } from "@/presentation/hooks/useRepresentativeLabels";
import { getCountryOptions, SUPPORTED_COUNTRY_CODES } from "@/utils/countries";
import { MONEY_DECIMAL_INPUT } from "@/utils/formatUtils";
import { usePromoterLabels } from "../hooks/usePromoterLabels";
import { CreateBeneficiaryLegalFields } from "./CreateBeneficiaryLegalFields";

type TextField = Exclude<
  keyof BeneficiaryWizardFormData,
  "companyType" | "incentiveType" | "activities" | "representative"
>;

export function CreateBeneficiaryStepFields({ step }: { step: number }) {
  const { t, i18n } = useTranslation();
  const { activitiesFor, positions } = usePromoterLabels();
  const representativeLabels = useRepresentativeLabels();
  const { register, control, formState } = useFormContext<BeneficiaryWizardFormData>();
  const errors = formState.errors;
  const verticals = activitiesFor(useWatch({ control, name: "incentiveType" }));
  const text = (
    key: TextField,
    label: string,
    placeholder: string,
    options: { required?: boolean; deps?: TextField[] } = {},
  ) => (
    <Field label={t(label)} required={options.required} error={errors[key]?.message}>
      <Input placeholder={t(placeholder)} {...register(key, { deps: options.deps })} />
    </Field>
  );
  switch (step) {
    case 0:
      return <CreateBeneficiaryLegalFields />;
    case 1:
      return (
        <>
          <Field
            label={t("promoters.attributes.vertical")}
            required
            error={errors.activities?.message}
          >
            <Controller
              control={control}
              name="activities"
              render={({ field }) => (
                <TagPicker
                  label={t("promoters.attributes.vertical")}
                  value={verticals
                    .filter((option) => field.value.includes(option.value))
                    .map((option) => option.label)}
                  options={verticals.map((option) => option.label)}
                  tone={(label) =>
                    verticals.find((option) => option.label === label)?.tone ?? "outline"
                  }
                  onChange={(labels) =>
                    field.onChange(
                      verticals
                        .filter((option) => labels.includes(option.label))
                        .map((option) => option.value),
                    )
                  }
                />
              )}
            />
          </Field>
          {text("cnae", "promoters.wizard.cnae", "promoters.wizard.cnae_placeholder")}
          <Field label={t("promoters.wizard.business_purpose")}>
            <Textarea
              placeholder={t("promoters.wizard.business_purpose_placeholder")}
              {...register("businessPurpose")}
            />
          </Field>
          <div className="grid gap-(--card-gap) sm:grid-cols-2">
            {text(
              "employees",
              "promoters.wizard.employees",
              "promoters.wizard.employees_placeholder",
            )}
            <Field label={t("promoters.wizard.revenue")} error={errors.revenue?.message}>
              <Controller
                control={control}
                name="revenue"
                render={({ field }) => (
                  <DecimalUnitInput
                    value={field.value}
                    onValueChange={field.onChange}
                    label={t("promoters.wizard.revenue")}
                    placeholder={t("promoters.wizard.revenue_placeholder")}
                    min="0"
                    {...MONEY_DECIMAL_INPUT}
                    minimumFractionDigits={0}
                  />
                )}
              />
            </Field>
          </div>
        </>
      );
    case 2:
      return (
        <>
          {text("address", "promoters.wizard.address", "promoters.wizard.address_placeholder")}
          <div className="grid gap-(--card-gap) sm:grid-cols-3">
            {text("city", "promoters.wizard.city", "promoters.wizard.city_placeholder")}
            {text(
              "postalCode",
              "promoters.wizard.postal_code",
              "promoters.wizard.postal_code_placeholder",
            )}
            {text("province", "promoters.wizard.province", "promoters.wizard.province_placeholder")}
          </div>
          <Field label={t("promoters.attributes.country")}>
            <Controller
              control={control}
              name="country"
              render={({ field }) => (
                <OptionSelect
                  value={field.value}
                  options={getCountryOptions(SUPPORTED_COUNTRY_CODES, i18n.language)}
                  label={t("promoters.attributes.country")}
                  onValueChange={(country) => {
                    if (country) field.onChange(country);
                  }}
                />
              )}
            />
          </Field>
          <div className="grid gap-(--card-gap) sm:grid-cols-2">
            {text("phone", "promoters.wizard.phone", "promoters.wizard.phone_placeholder")}
            {text("email", "promoters.wizard.email", "promoters.wizard.email_placeholder")}
          </div>
          {text("website", "promoters.wizard.website", "promoters.wizard.website_placeholder")}
        </>
      );
    case 3:
      return (
        <RepresentativeFields
          name="representative"
          positions={positions}
          labels={representativeLabels}
        />
      );
    default:
      return (
        <>
          {text("bankName", "promoters.wizard.bank", "promoters.wizard.bank_placeholder", {
            deps: ["iban"],
          })}
          {text("iban", "promoters.wizard.iban", "promoters.wizard.iban_placeholder")}
          {text("swift", "promoters.wizard.swift", "promoters.wizard.swift_placeholder", {
            deps: ["iban"],
          })}
        </>
      );
  }
}
